"use client";

import React from "react";
import { endOfQuarter, format } from "date-fns";
import cx from "classnames";

import { Swiper as SwiperReact, SwiperSlide, useSwiper } from "swiper/react";
import { Controller, Navigation, EffectFade } from "swiper/modules";

import Icon from "@ide/shared/components/Icon";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/effect-fade";

// https://dribbble.com/shots/3730753-Month-Quarter-selector
const QuarterSelector = ({
  value = null,
  startYear = new Date().getFullYear(),
  yearsRange = 6,
  onChange = (date) => {},
}) => {
  const [year, setYear] = React.useState(value ? new Date(value).getFullYear() : startYear);
  const swiperYears = React.useRef();

  const years = React.useMemo(() => {
    const tmp = [startYear];

    for (let i = 1; i < yearsRange; i++) {
      tmp.push(startYear + i);
    }

    return tmp;
  }, [startYear]);

  const [controlledSwiper, setControlledSwiper] = React.useState(null);

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative max-w-64">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-full bg-neutral-100 rounded-sm"></div>

        {year > startYear && (
          <button
            onClick={() => swiperYears.current.slidePrev()}
            className="transition-all absolute w-6 h-6 left-0 top-1/2 -translate-x-full -translate-y-1/2 cursor-pointer hover:scale-110"
          >
            <Icon icon="fa-regular fa-chevron-left" />
          </button>
        )}

        {year < startYear + yearsRange - 1 && (
          <button
            onClick={(e) => swiperYears.current.slideNext()}
            className="transition-all absolute w-6 h-6 right-0 top-1/2 translate-x-full -translate-y-1/2 cursor-pointer hover:scale-110"
          >
            <Icon icon="fa-regular fa-chevron-right" />
          </button>
        )}

        <SwiperReact
          className="relative"
          slidesPerView={3}
          centeredSlides
          initialSlide={years.indexOf(year)}
          modules={[Controller]}
          controller={{ control: controlledSwiper }}
          onSwiper={(swiper) => (swiperYears.current = swiper)}
          slideToClickedSlide
          onActiveIndexChange={(s) => {
            setYear(years[s.activeIndex]);
          }}
        >
          {years.map((y) => (
            <SwiperSlide key={`quarter-select-year-${y}`} className="cursor-pointer">
              <div className="flex justify-center">
                <span
                  className={cx({
                    "text-gray-600": year !== y,
                    "font-semibold": year === y,
                  })}
                >
                  {y}
                </span>
              </div>
            </SwiperSlide>
          ))}
        </SwiperReact>
      </div>

      <SwiperReact
        slidesPerView={1}
        centeredSlides
        modules={[Controller, EffectFade]}
        onSwiper={setControlledSwiper}
        // crossFade
        effect="fade"
      >
        {years.map((y) => (
          <SwiperSlide key={`quarter-select-${y}`}>
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4).keys()].map((q) => (
                <button
                  key={`quarter-select-${y}-${q}`}
                  onClick={() => {
                    if (value === format(endOfQuarter(new Date(year, q * 3, 1)), "yyyy-MM-dd")) {
                      onChange(undefined);
                    } else {
                      onChange(format(endOfQuarter(new Date(year, q * 3, 1)), "yyyy-MM-dd"));
                    }
                  }}
                  className={cx("flex flex-col p-4 bg-neutral-100 rounded-sm", {
                    "border border-cyan-400":
                      value === format(endOfQuarter(new Date(year, q * 3, 1)), "yyyy-MM-dd"),
                  })}
                >
                  <span className="text-xl font-medium">Q{q + 1}</span>
                  <span>{y}</span>
                </button>
              ))}
            </div>
          </SwiperSlide>
        ))}
      </SwiperReact>
    </div>
  );
};

export default QuarterSelector;
